import type { WebSocketMessage, WebSocketEventType } from '@/types/websocket'

type MessageCallback = (event: MessageEvent<WebSocketMessage>) => void

const isClient = typeof window !== 'undefined'

const buildWebSocketUrl = (token: string): string => {
  const baseUrl = new URL(import.meta.env.VITE_API_URL)
  const sanitizedPath = baseUrl.pathname.replace(/\/$/, '')

  baseUrl.pathname = `${sanitizedPath}/websocket/connect`
  baseUrl.searchParams.set('token', `Bearer ${token}`)
  baseUrl.protocol = baseUrl.protocol === 'https:' ? 'wss:' : 'ws:'

  return baseUrl.toString()
}

class WebSocketService {
  private socket: WebSocket | null = null
  private listeners = new Set<MessageCallback>()
  private statusListeners = new Set<(connected: boolean) => void>()
  private reconnectAttempts = 0
  private reconnectTimer: number | null = null
  private readonly maxReconnectAttempts = 5
  private readonly reconnectInterval = 3000
  private lastToken: string | null = null

  async connect(token: string): Promise<void> {
    if (!isClient) {
      return
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      return
    }

    this.lastToken = token

    await new Promise<void>((resolve, reject) => {
      const url = buildWebSocketUrl(token)
      let settled = false

      try {
        this.socket = new WebSocket(url)
      } catch (error) {
        this.socket = null
        reject(error)
        return
      }

      const handleOpen = () => {
        settled = true
        this.reconnectAttempts = 0
        this.notifyStatusChange(true)
        resolve()
      }

      const handleMessage = (event: MessageEvent) => {
        this.listeners.forEach((listener) => {
          try {
            listener(event as MessageEvent<WebSocketMessage>)
          } catch (listenerError) {
            console.error('WebSocket listener failed:', listenerError)
          }
        })
      }

      const handleClose = () => {
        this.cleanupSocketListeners(handleOpen, handleMessage, handleClose, handleError)
        this.socket = null
        this.notifyStatusChange(false)
        this.scheduleReconnect()
      }

      const handleError = (event: Event) => {
        console.error('WebSocket error:', event)
        if (!settled) {
          settled = true
          this.cleanupSocketListeners(handleOpen, handleMessage, handleClose, handleError)
          this.socket = null
          reject(event)
        }
      }

      this.socket.addEventListener('open', handleOpen)
      this.socket.addEventListener('message', handleMessage)
      this.socket.addEventListener('close', handleClose)
      this.socket.addEventListener('error', handleError)
    })
  }

  subscribe(callback: MessageCallback): () => void {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }

  subscribeStatus(callback: (connected: boolean) => void): () => void {
    this.statusListeners.add(callback)
    return () => {
      this.statusListeners.delete(callback)
    }
  }

  send(payload: string | Record<string, unknown>): void {
    if (this.socket?.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not connected')
      return
    }

    try {
      const data = typeof payload === 'string' ? payload : JSON.stringify(payload)
      this.socket.send(data)
    } catch (error) {
      console.error('Failed to send WebSocket message:', error)
    }
  }

  disconnect(): void {
    if (this.reconnectTimer && isClient) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    this.lastToken = null

    if (this.socket) {
      this.socket.close()
      this.socket = null
    }

    this.notifyStatusChange(false)
  }

  get isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  get readyState(): number | undefined {
    return this.socket?.readyState
  }

  private cleanupSocketListeners(
    handleOpen: () => void,
    handleMessage: (event: MessageEvent) => void,
    handleClose: () => void,
    handleError: (event: Event) => void
  ) {
    if (!this.socket) {
      return
    }

    this.socket.removeEventListener('open', handleOpen)
    this.socket.removeEventListener('message', handleMessage as EventListener)
    this.socket.removeEventListener('close', handleClose)
    this.socket.removeEventListener('error', handleError)
  }

  private scheduleReconnect(): void {
    if (!isClient || !this.lastToken) {
      return
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max WebSocket reconnection attempts reached')
      return
    }

    this.reconnectAttempts += 1

    this.reconnectTimer = window.setTimeout(() => {
      if (!this.lastToken) {
        return
      }

      this.connect(this.lastToken).catch((error) => {
        console.error('WebSocket reconnection failed:', error)
      })
    }, this.reconnectInterval)
  }

  private notifyStatusChange(connected: boolean): void {
    this.statusListeners.forEach((listener) => {
      try {
        listener(connected)
      } catch (listenerError) {
        console.error('WebSocket status listener failed:', listenerError)
      }
    })
  }
}

export const webSocketService = new WebSocketService()
